// MultiCdmSettingsPanel.js
// -----------------------------------------------------------------------------
// The per-session settings form. Used in two places:
//
//   1. MultiCdmCaseBuilder — inline, while the block is being put together.
//   2. MultiCdmBreakScreen — inside a modal, between cases.
//
// It's a controlled component: it never stores settings itself, it just renders
// the object it's given and calls onChange with the next one. Persistence is the
// caller's business (the builder holds them until create; the break screen saves
// them straight to the session).
//
// Differences from Multi CCS's settings panel:
//   - "Time limit" is a speed multiplier (TimerSpeed), not a minutes/percentage
//     dropdown — CDM has no static per-case default to offset against; the
//     block just runs App.js's own getTimerSpeedMultiplier() against whatever
//     TOTALMINFORCASE the case reports when it starts. No free-text custom
//     box, since there's nothing to type a number of minutes into.
//   - No "Simulate network lag" control — CDM's <Simulation> has no equivalent
//     to CCS's networkLag prop, so the setting had nothing to attach to.
//
// Props:
//   settings   object                                    (required)
//   onChange   (nextSettings) => void                    (required)
//   disabled   bool — lock the form while saving         (optional)
// -----------------------------------------------------------------------------

import React from "react";
import { TIMER_SPEED_OPTIONS } from "./multiCdmFetches";

const BREAK_MINUTE_OPTIONS = [1, 2, 5, 10, 15];

class MultiCdmSettingsPanel extends React.Component {
  patch = (changes) => {
    this.props.onChange(Object.assign({}, this.props.settings, changes));
  };

  render() {
    const settings = this.props.settings;
    const disabled = !!this.props.disabled;

    return (
      <div className="mcdm-settings">
        <div className="mcdm-field">
          <label className="mcdm-check">
            <input
              type="checkbox"
              checked={settings.EnforceCaseTimeLimit}
              disabled={disabled}
              onChange={(e) => {
                this.patch({ EnforceCaseTimeLimit: e.target.checked });
              }}
            />
            Enforce the time limit on each case
          </label>
          <p className="mcdm-hint">
            {settings.EnforceCaseTimeLimit
              ? "When the clock runs out, the case ends and is graded automatically."
              : "Cases run untimed. You end each one yourself."}
          </p>
        </div>

        {settings.EnforceCaseTimeLimit && (
          <div className="mcdm-field">
            <label className="mcdm-label" htmlFor="mcdm-timer-speed">
              Timer speed
            </label>
            <select
              id="mcdm-timer-speed"
              className="mcdm-select"
              disabled={disabled}
              value={settings.TimerSpeed}
              onChange={(e) => {
                this.patch({ TimerSpeed: e.target.value });
              }}
            >
              {TIMER_SPEED_OPTIONS.map(function (speed) {
                return (
                  <option key={speed} value={speed}>
                    {speed}
                    {speed === "1x" ? " (normal)" : ""}
                  </option>
                );
              })}
            </select>
            <p className="mcdm-hint">
              Each case keeps its own real allotment — this only speeds up or
              slows down the clock against it, the same as the single-case
              timer speed setting.
            </p>
          </div>
        )}

        <div className="mcdm-field">
          <label className="mcdm-check">
            <input
              type="checkbox"
              checked={settings.EnforceBreak}
              disabled={disabled}
              onChange={(e) => {
                this.patch({ EnforceBreak: e.target.checked });
              }}
            />
            Time the break between cases
          </label>
          <p className="mcdm-hint">
            {settings.EnforceBreak
              ? "The next case starts on its own when the break runs out."
              : "Breaks are untimed. You start the next case when you're ready."}
          </p>
        </div>

        {settings.EnforceBreak && (
          <div className="mcdm-field">
            <label className="mcdm-label" htmlFor="mcdm-break-minutes">
              Break length
            </label>
            <select
              id="mcdm-break-minutes"
              className="mcdm-select"
              disabled={disabled}
              value={String(settings.BreakMinutes)}
              onChange={(e) => {
                this.patch({ BreakMinutes: parseInt(e.target.value, 10) });
              }}
            >
              {BREAK_MINUTE_OPTIONS.map(function (minutes) {
                return (
                  <option key={minutes} value={String(minutes)}>
                    {minutes} {minutes === 1 ? "minute" : "minutes"}
                  </option>
                );
              })}
            </select>
          </div>
        )}

        <div className="mcdm-field">
          <span className="mcdm-label">On the break screen</span>
          <label className="mcdm-check">
            <input
              type="checkbox"
              checked={settings.HideCaseTitle}
              disabled={disabled}
              onChange={(e) => {
                this.patch({ HideCaseTitle: e.target.checked });
              }}
            />
            Hide case titles
          </label>
          <label className="mcdm-check">
            <input
              type="checkbox"
              checked={settings.HideCaseCategory}
              disabled={disabled}
              onChange={(e) => {
                this.patch({ HideCaseCategory: e.target.checked });
              }}
            />
            Hide case categories
          </label>
          <p className="mcdm-hint">
            Hiding both keeps you from guessing at a case before you start it.
          </p>
        </div>
      </div>
    );
  }
}

export default MultiCdmSettingsPanel;
